import Dexie from 'dexie'
import { createStore } from '../lib/store'
import type { Table } from 'dexie'

/**
 * One row per site-facing command that could not reach the server (design
 * §8.2; architecture §8: "a Dexie outbox for site-facing writes only ...
 * queued with their idempotency keys and replayed on reconnect"). Every
 * field here is exactly what a replay needs to rebuild the ORIGINAL request
 * byte-for-byte — nothing is recomputed at drain time.
 *
 * `idempotencyKey` is the load-bearing field: `enqueue.ts`'s own doc comment
 * has the full reasoning, but the short version is that at-least-once
 * delivery is only safe because a replayed command carries the SAME key the
 * appender's `command_log` dedupe already saw, and that key is minted once,
 * by the CALLER (`api/hooks.ts`'s `commandHook`), before this row is ever
 * written.
 */
export interface OutboxEntry {
  readonly idempotencyKey: string
  readonly command: string
  readonly payload: unknown
  readonly clientBuild: string
  readonly enqueuedAt: string
  status: 'queued' | 'refused'
  /**
   * EVERY reason the server gave, not just one — fix round 1's Important 3,
   * fixed here after being measured: a response can carry several `Failure`s
   * at once (`combineGates` — "Collects EVERY failure. Requirement I6 must
   * name all outstanding items."), and the ONLY other place a refusal is
   * rendered (`test/data-layer.test.tsx`'s `DraftScreen`, the online path)
   * already maps over all of `response.failures`. A single `refusal` object
   * here meant the tray showed strictly LESS than the online path for the
   * identical response — `drain.ts`'s own `sweep()` used to take only
   * `failures[0]`, discarding the rest. Plural, and named `refusals` (not
   * `refusal`) so a reader is not tempted to reach for `.code`/`.message`
   * directly on it.
   */
  refusals?: readonly { code: string; message: string }[]
  /**
   * Final review bucket 1 item 5 — how many times a RECOVERABLE http
   * failure (`drain.ts`'s own `isRecoverableHttpFailure`: 408, 429, any 5xx,
   * and a 401 whose `reason` is NOT `'disabled'` — a class the server
   * genuinely might answer differently to LATER, unlike a permanent 4xx)
   * has been attempted for this row.
   *
   * The `disabled` carve-out is not a detail: web-pre-deploy item 4 made a
   * revoked account's 401 PERMANENT, so it promotes straight to `refused`
   * with a real sentence instead of backing off forever under "Waiting to
   * send". This sentence read "401, 408, 429, or any 5xx" until the
   * final-review fix wave, which is what the class was BEFORE that item
   * landed — `drain.ts`'s `isRecoverableHttpFailure` is the authority.
   * `undefined` means "never failed recoverably yet". Feeds `retryAfter`
   * below; never read for anything else, and never shown on any screen.
   */
  attempts?: number
  /**
   * Set alongside `attempts` by `drain.ts`'s `markRetry`. An ISO instant
   * before which `sweep()` skips this row entirely on its next pass, so a
   * still-`queued`, recoverably-failing row is not resent on every single
   * `online`/`visibilitychange` trigger forever — Task 17's own "one
   * poison entry loops forever" concern, restated for a status class that
   * legitimately CAN recover (a 401 once the session is refreshed by a
   * fresh sign-in; a 429 once the rate limit window passes; a 5xx once the
   * server recovers), where retrying immediately, every trigger, would be
   * actively counterproductive rather than merely wasteful (hammering a
   * rate limiter makes it worse, not better).
   */
  retryAfter?: string
}

/**
 * The Dexie schema. `idempotencyKey` primary — there is exactly one row per
 * command, by construction (`enqueue.ts` upserts on this key rather than
 * ever inserting a second row for one). `status` indexed so `drain()` (and
 * Task 18's Sync tray, which reads it and — via `enqueue.ts`'s `discard()`,
 * once a refusal is repaired or dismissed — is the one OTHER writer besides
 * `enqueue()`/`drain()` itself) can each query "still queued" / "refused,
 * needs a human" without a full table scan.
 */
class OutboxDatabase extends Dexie {
  entries!: Table<OutboxEntry, string>

  constructor(name: string) {
    super(name)
    this.version(1).stores({ entries: 'idempotencyKey, status' })
  }
}

const DB_NAME = 'gridfit-outbox'

// MODULE-SCOPE SINGLETON, deliberately — this codebase's established shape
// for client state several call sites need without prop-drilling
// (`pwa/skew.ts`'s own top comment: "ONE module-scope store ... read from
// ONE screen"). `resetOutboxForTests` below exists because of exactly this:
// a database left open (or left with rows in it) from one test is visible
// to the very next one that calls `outboxDb()` again, unless something
// closes AND deletes it first — the same shape `resetRegistryForTests`,
// `resetFoldsForTests` and `resetQueriesForTests` already carry for their
// own module-scope state, for the identical reason.
let instance: OutboxDatabase | undefined

/**
 * Lazily constructs the singleton — NOT at this module's own top level.
 * `enqueue.ts`/`drain.ts` both import this module, and so does `api/
 * hooks.ts` (to wire the network-failure branch), which is imported by
 * essentially every app-integration test in this suite. If opening the real
 * IndexedDB connection happened as a module-scope side effect of merely
 * IMPORTING this file, every one of those tests would try to open a real
 * `indexedDB` the moment its module graph loaded — and only the handful of
 * test files that need it install `fake-indexeddb` (`outbox.test.tsx`,
 * `outbox-cross-tab.test.tsx`, `sync-tray.test.tsx`), each per-file
 * deliberately; see `outbox.test.tsx`'s own top comment for why it is not in
 * the shared `test/setup.ts`. Constructing here, on first CALL not first IMPORT,
 * means every other test file's `indexedDB` global stays exactly as absent
 * as it always was — harmless, because nothing in those files ever calls
 * `enqueue`/`pending`/`drain`, or fires the `online`/`visibilitychange`
 * events that would (`startOutboxAutoDrain`, wired from `main.tsx`, not
 * `hooks.ts` — review round 1 moved it: see that file's own top comment).
 */
export function outboxDb(): OutboxDatabase {
  if (!instance) {
    instance = new OutboxDatabase(DB_NAME)
    hydrateCounts()
  }
  return instance
}

/**
 * A synchronous mirror of "how many rows are still `queued` right now" —
 * `isOutboxEmpty()` (`pwa/deviceMode.ts`) needs a plain, synchronous
 * `boolean`: IndexedDB has no synchronous read API, so this cannot be
 * answered by querying Dexie directly from that call site.
 *
 * `queued` ONLY, NOT `refused` — review round 1's Important 2 (fixed here
 * after being measured): a `refused` row is TERMINAL from an automatic
 * retry's point of view (nothing here retries it; Task 18's Sync tray is
 * what a human uses to review and clear it), so counting it toward "not
 * empty" makes it permanently block device-mode auto-accept for a reason a
 * RELOAD cannot fix — measured worst case: a `clientBuild` below the
 * server's floor 426s every queued command, `drain()` (correctly, per the
 * fix below) marks each `refused` rather than leaving it `queued` forever,
 * but the OLD "any row counts" version of this mirror still reported
 * non-empty afterwards — so the kiosk never auto-accepted the very update
 * that would have cleared the floor. Only a GENUINELY queued row — one a
 * FUTURE drain might still succeed at, given time or a build update — has
 * any business blocking a reload.
 *
 * Kept in step with the table by the three mutation points that can change
 * how many rows are `queued` — `enqueue()` (a genuinely NEW queued row),
 * `drain()`'s delete-on-success, and `drain()`'s mark-as-`refused` (no
 * longer queued, even though the row itself survives) — via
 * `noteRowAdded`/`noteRowRemoved` below.
 *
 * `null` until the first read of the real table resolves ("not yet
 * hydrated"), not `0` — `outboxIsEmpty()` treats `null` as NOT empty
 * (`rowCount === 0` is false for `null`), which is the conservative
 * direction: a kiosk that has just booted and has not yet heard back from
 * its own IndexedDB about rows a PREVIOUS session may have left behind
 * blocks auto-accept until it genuinely knows, rather than assuming empty
 * and risking a reload that strands those rows' `clientBuild` behind a
 * floor the new build no longer accepts. The read this hydration kicks off
 * is a single, fast IndexedDB `count()` — resolving in low milliseconds
 * against `IDLE_UPDATE_MS`'s two-minute scale, not a residual left
 * unaddressed.
 */
let rowCount: number | null = null

/**
 * A second, sibling mirror — Task 18. Same shape and same reason as
 * `rowCount` above (IndexedDB has no synchronous read), but for `refused`
 * rows instead of `queued` ones: the Sync tray's nav badge (`shell/
 * SyncTray.tsx`) needs a synchronous "how many rows need a human right now"
 * that adds BOTH mirrors together, and `useSyncExternalStore` needs a
 * snapshot getter that never touches IndexedDB directly. Kept as its own
 * variable rather than folded into `rowCount` because `outboxIsEmpty()`'s
 * whole point (this file's own `rowCount` comment, Task 17 review round 1)
 * is that a `refused` row must NOT count toward "not empty" — merging the
 * two mirrors would resurrect exactly that bug.
 */
let refusedCount: number | null = null

type OutboxCounts = { readonly queued: number; readonly refused: number }

/**
 * Task 18's badge/tray subscription — `lib/store.ts`'s shared helper as of
 * web-pre-deploy item 9, where this file used to hand-roll the
 * `listeners`/`subscribe`/`getSnapshot` shape itself, citing `pwa/skew.ts`
 * by name as its precedent ("ONE module-scope store, written from several
 * places, read from one screen"). Here the several writers are `enqueue()`,
 * `drain()`'s two transitions and `discard()`; the one reader is
 * `SyncTray`'s badge/list.
 *
 * THE CACHED VALUE IS THE WHOLE POINT, and it is now the helper's own
 * guaranteed property rather than this file's private discipline: a fresh
 * `{ queued, refused }` object is built only when `notifyOutboxChanged()`
 * runs, never on every read. `useSyncExternalStore` compares what its
 * `getSnapshot` returns via `Object.is` to decide whether anything changed,
 * and a getter that builds the object anew on every call (this mirror's own
 * first version) never returns the SAME reference twice — React sees
 * "changed" on every single render, calls `getSnapshot` again to confirm,
 * sees "changed" again, and spins forever. MEASURED directly (see Task 18's
 * report): every app-integration test mounting `AppShell` (i.e. nearly all
 * of them) failed with React's own "Maximum update depth exceeded" the
 * moment `SyncTray` first subscribed, before the cache existed.
 * `test/store.test.ts`'s second case is what now keeps that property from
 * being lost again, in one place, for all four stores.
 */
const countsStore = createStore<OutboxCounts>({ queued: 0, refused: 0 })

/**
 * Rebuilds the snapshot from the two mirrors and notifies the tray — ONE
 * operation now, where this file used to have `recomputeSnapshot()` and a
 * separate notify loop. The store deliberately offers no way to change the
 * held value silently: a rebuilt-but-unannounced snapshot is a value that
 * lies to every already-subscribed component until some unrelated mutation
 * happens to notify, which is a worse default than one redundant render.
 *
 * `null` (not yet hydrated) publishes as `0` — the same conservative-for-
 * blocking-nothing direction `outboxIsEmpty()` takes for `rowCount` (a badge
 * that hasn't heard back yet shows nothing rather than a stale guess).
 *
 * EXPORTED as of web-pre-deploy items 1/2 — `drain.ts`'s `markRefused` calls
 * this DIRECTLY (not through one of the `note*` functions below) for a
 * resend that is refused again. That path changes a row's CONTENT (a new
 * set of reasons) with NO change to either count — the row was already
 * `refused`, still is — so neither `noteRowRemoved()` nor
 * `noteRefusedAdded()` applies (see that function's own comment for why
 * calling either would drift the mirrors). But `SyncTray.tsx`'s own fetch
 * effect needs telling regardless, since the row it should re-fetch is the
 * only thing that changed. This function always builds a FRESH object
 * literal, so `countsStore.set`'s `Object.is` guard never suppresses it —
 * every call here republishes, even when `rowCount`/`refusedCount`
 * themselves come out identical to what was already held, which is exactly
 * the property this second caller depends on.
 */
export function notifyOutboxChanged(): void {
  countsStore.set({ queued: rowCount ?? 0, refused: refusedCount ?? 0 })
}

export function subscribeOutboxCounts(listener: () => void): () => void {
  return countsStore.subscribe(listener)
}

/** `useSyncExternalStore`'s snapshot getter — the CACHED reference, never a freshly-built object; see `countsStore`'s own comment for why that distinction is a measured infinite-loop bug rather than a style preference. */
export function outboxCountsSnapshot(): OutboxCounts {
  return countsStore.getSnapshot()
}

function hydrateCounts(): void {
  // `instance` is guaranteed set by `outboxDb()`'s own caller, immediately
  // above the one call site that invokes this. Both counts are hydrated
  // together, from the SAME table, so the tray's badge never shows one
  // freshly-hydrated number next to one still-null one.
  const table = (instance as OutboxDatabase).entries
  void Promise.all([table.where('status').equals('queued').count(), table.where('status').equals('refused').count()])
    .then(([queued, refused]) => {
      rowCount = queued
      refusedCount = refused
      notifyOutboxChanged()
    })
    .catch(() => {
      // Review round 1, Important 4 (fixed here after being measured): an
      // IndexedDB fault (Safari private mode, evicted storage, a blocked
      // schema upgrade) rejecting this `count()` must not leave `rowCount`
      // stuck at `null` — `outboxIsEmpty()` below treats `null` as NOT
      // empty, so a permanently-null count PERMANENTLY blocks device-mode
      // auto-accept on a device whose storage is simply broken, with no
      // possible drain ever fixing that. Treated as `0`: with no
      // FUNCTIONING outbox at all, nothing could reliably have been queued
      // into it either, so "empty" is the same accurate answer
      // `noIndexedDbAvailable()` gives for the "no indexedDB at all" case.
      //
      // NOT verified to prevent an unhandled-rejection report the way the
      // identical-looking fix in `drain.ts`'s `startOutboxAutoDrain`
      // listeners does — measured directly (see the task report): this
      // query runs OUTSIDE a Dexie transaction, and Dexie's own internal
      // unhandled-promise tracking (`globalPSD.onunhandled`) is a no-op at
      // that scope, so removing this `.catch()` entirely produced NO
      // observable `process.on('unhandledRejection')`/`window.
      // onunhandledrejection` event in direct experimentation, with or
      // without this handler. The listeners' catches ARE independently
      // verified (their own covering test): `sweep()`/`pending()` are
      // native `async function`s, and returning a Dexie thenable from one
      // adopts its rejection into a genuine native `Promise` via the
      // engine's own await/return machinery, which IS what Node's and the
      // browser's unhandled-rejection tracking observes. This handler's
      // real, MEASURED job is the `rowCount` recovery above, not rejection
      // suppression — kept anyway because leaving it out lets an
      // already-rejected promise sit unresolved with a `void` in front of
      // it, which is still worth resolving cleanly.
      rowCount = 0
      refusedCount = 0
      notifyOutboxChanged()
    })
}

/**
 * `true` if this environment has no `indexedDB` global at all — checked
 * BEFORE `outboxIsEmpty()` ever constructs the Dexie singleton itself
 * (below), for two reasons at once:
 *
 *  1. Correctness in a real, if vanishingly rare, browser without
 *     IndexedDB support: with no possible backing store, no outbox entry
 *     could ever exist, so "empty" is the factually correct answer, not a
 *     conservative guess.
 *  2. Safety in THIS test suite: every OTHER app-integration test file
 *     (`update-prompt.test.tsx` among them) exercises `isDeviceMode`'s
 *     auto-accept path — and so calls this function — WITHOUT installing
 *     `fake-indexeddb` (only the three outbox/tray files do; see `outboxDb`'s
 *     own comment for the list). Before this check existed, THOSE tests
 *     regressed: `outboxIsEmpty()` read `rowCount` before anything had ever
 *     hydrated it, staying `null` (⇒ not empty) forever, and every one of
 *     Task 16's auto-accept assertions started failing — measured directly,
 *     see the task report. Real browsers always have `indexedDB`, so this
 *     branch is inert in production; it exists for this environment only.
 *
 * EXPORTED as of Task 18: `SyncTray.tsx`'s own entry-fetching effect needs
 * the identical guard, for the identical reason, MEASURED again in this
 * task — `SyncTray` mounts inside `AppShell` on every `renderApp()` call
 * now, so every app-integration test file that has never installed
 * `fake-indexeddb` (nearly all of them) hit an unhandled `MissingAPIError`
 * the moment that effect called `pending()`/`refused()` unconditionally.
 */
export function noIndexedDbAvailable(): boolean {
  return typeof indexedDB === 'undefined'
}

export function outboxIsEmpty(): boolean {
  if (noIndexedDbAvailable()) return true
  // Lazily constructs the singleton and kicks off `hydrateCounts` on the
  // very FIRST call, rather than requiring some OTHER caller
  // (`enqueue`/`pending`/`drain`) to have already touched the table.
  // Without this, a device-mode session that has never yet had a command
  // fail — the common case — would call this function long before anything
  // else ever calls `outboxDb()`, and `rowCount` would sit at `null`
  // (blocking auto-accept) forever. `outboxDb()` itself is idempotent past
  // the first call, so calling it here on every tick costs nothing once the
  // singleton already exists.
  outboxDb()
  return rowCount === 0
}

/** Called by `enqueue()` after inserting a row that did not exist before. */
export function noteRowAdded(): void {
  rowCount = (rowCount ?? 0) + 1
  notifyOutboxChanged()
}

/**
 * Called by `drain()` once per row it takes out of the `queued` state —
 * either by deleting it (`ok: true`) or by marking it `refused` (`ok:
 * false`, or a caught `ApiError`). NOT clamped at zero (`Math.max(0, ...)`,
 * review round 1's own words: "a symptom clamp, not a fix") — that clamp
 * used to paper over `drain()` running re-entrantly (two overlapping
 * sweeps both decrementing the same row) rather than actually preventing
 * it. With `drain()`'s own in-flight guard (this file's sibling,
 * `drain.ts`) making a second concurrent sweep return the FIRST sweep's
 * promise instead of starting a second one, and each row appearing in at
 * most one sweep's `pending()` snapshot, this can only ever be called once
 * per row per genuine removal — a negative count would now be a real bug
 * to see, not a symptom to hide.
 */
export function noteRowRemoved(): void {
  rowCount = (rowCount ?? 0) - 1
  notifyOutboxChanged()
}

/** Called by `drain()`'s `markRefused` after a row transitions INTO `refused` (never called for a row that was already refused — `markRefused` runs once per row per sweep). */
export function noteRefusedAdded(): void {
  refusedCount = (refusedCount ?? 0) + 1
  notifyOutboxChanged()
}

/**
 * Called by `discard()` (`enqueue.ts`, Task 18) after removing a `refused`
 * row — a human repaired it (the registered `RepairComponent`'s resubmission
 * reached the server) or dismissed it outright.
 *
 * CORRECTED (fix round 1): this comment used to claim "`discard()` reads
 * the row's actual stored status before deciding which counter to touch, so
 * this is only ever called once per row per genuine removal" — true only
 * for a single, sequential call. Fix round 1 measured that TWO concurrent
 * `discard()` calls for the SAME key (a double-tap on "Dismiss" — a real,
 * reachable UI race, not contrived) each ran their OWN `get()` before
 * either had deleted anything, so both saw the row and both called this
 * function: `refusedCount` dropped by 2 for one row leaving. The actual
 * guarantee "only once per genuine removal" now depends on `discard()`'s
 * OWN fix (`enqueue.ts` — wrapping the get/delete/decrement in one Dexie
 * `'rw'` transaction, which IndexedDB will not let interleave with another
 * `'rw'` transaction on the same store), not on this function's own no-
 * clamp shape. Kept unclamped regardless: with that fix in place, a
 * negative count would once again be a real bug worth seeing.
 */
export function noteRefusedRemoved(): void {
  refusedCount = (refusedCount ?? 0) - 1
  notifyOutboxChanged()
}

/**
 * Closes the current connection and deletes the underlying IndexedDB
 * database outright — not merely `.clear()`ing the table — so the NEXT
 * `outboxDb()` call opens a genuinely empty store. `test/outbox.test.tsx`
 * calls this between cases; without it, `fake-indexeddb`'s backend (shared
 * for the whole test FILE, same as real IndexedDB is shared for the whole
 * browser origin) keeps rows from one test visible to the next, which is
 * exactly the flake source CLAUDE.md's "a flaky test is a determinism bug"
 * rules out.
 *
 * Sets `rowCount` back to `null` ("not yet hydrated"), NOT `0` — the very
 * next `outboxDb()` call constructs a brand new instance and calls
 * `hydrateCounts()` on it regardless (`outboxDb()`'s own `if (!instance)`
 * branch), so whatever this function claimed synchronously would be
 * overwritten by a real re-hydration a moment later anyway. Claiming `0`
 * here bought nothing and cost something: `test/outbox.test.tsx`'s own
 * `hydrateRowCount` recovery case needs `rowCount` to start genuinely
 * unknown so a mutation that stops `hydrateCounts`'s `.catch` from
 * setting it can be OBSERVED — with this function pre-seeding `0`, that
 * case passed whether or not the mutation was applied (measured directly:
 * the `0` this function used to write was indistinguishable from the `0`
 * a correctly-recovering hydration writes).
 *
 * `db.delete()`'s failure is swallowed, not propagated — `instance` is
 * cleared UNCONDITIONALLY above it either way, which is the part that
 * actually matters for the next test's isolation. Added after being
 * measured: `test/outbox.test.tsx`'s own "no working IndexedDB" case
 * deliberately breaks `Dexie.dependencies.indexedDB` for one instance —
 * that instance's `_deps.indexedDB` is a snapshot frozen at CONSTRUCTION
 * time (Dexie's own source), so restoring the global afterwards does not
 * un-break the instance already built while it was broken. Without this
 * `try/catch`, THIS function threw `Cannot read properties of undefined
 * (reading 'deleteDatabase')` on the very next test's `beforeEach` — a
 * failure in the test harness's OWN cleanup, not in anything the next test
 * was trying to prove.
 */
export async function resetOutboxForTests(): Promise<void> {
  if (!instance) return
  const db = instance
  instance = undefined
  rowCount = null
  // Task 18: `refusedCount` gets the identical "back to not-yet-hydrated"
  // treatment as `rowCount` above, for the identical reason — the next
  // `outboxDb()` call re-hydrates both together (`hydrateCounts`), so
  // claiming a stale `0` here would be indistinguishable from a correctly
  // recovered `0` and cost a future test's ability to see that recovery
  // fail.
  refusedCount = null
  // Task 18: the CACHED snapshot does not update itself just because
  // `rowCount`/`refusedCount` changed above — it is only ever rebuilt inside
  // `notifyOutboxChanged()`. Without this call, a test's `beforeEach` reset
  // would leave `outboxCountsSnapshot()` reporting the PREVIOUS test's
  // counts until the next real mutation, and a `SyncTray` mounted fresh in
  // the very next test would read a stale badge number on its first render.
  //
  // DELIBERATELY NOT `countsStore.reset()` — web-pre-deploy item 9. That
  // would also drop every live subscriber, which is the right behaviour for
  // `pwa/skew.ts` and `outbox/clearance.ts` (whose renamed resets now say so
  // out loud) but not here: this function is the only one of the three that
  // re-PUBLISHES rather than merely clearing, so a `SyncTray` still mounted
  // across the call is told about the zeroes instead of being silently
  // orphaned. Keeping the subscribers is what lets this export keep the
  // plain `resetXForTests` name the seven `core`/`contracts` instances
  // established.
  notifyOutboxChanged()
  try {
    await db.delete()
  } catch {
    // See the doc comment above — a delete that cannot reach an
    // intentionally-broken backend has nothing real left to clean up;
    // `instance` above is already the part that gives the next test a
    // genuinely fresh singleton.
  }
}
